import React, { useEffect, useMemo, useState } from 'react';

import i18n from 'i18n-js';
import {
    Button,
    Chip,
    FormControl,
    Input,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Toolbar,
    Typography,
    makeStyles,
} from '@material-ui/core';
import EmailOutlinedIcon from '@material-ui/icons/EmailOutlined';
import HighlightOffOutlinedIcon from '@material-ui/icons/HighlightOffOutlined';

import agent from 'api/agent';
import {
    CustomerManagementFilterModel,
    CustomerManagementListItemData,
    CustomerManagementPagedModel,
} from 'api/models';
import { images } from 'assets';
import commonStyles from 'config/commonStyles';
import { pagingOptions } from 'constants/arrays';
import { theme } from 'config/theme';
import { useDispatch } from 'react-redux';
import globalStore from 'store/globalStore';
import {
    BasicThreeDotsMenu,
    NothingFoundMessage,
    SearchInput,
    StyledChip,
    TableToolbar,
    Text,
} from 'components/shared';

const useStyles = makeStyles({
    ...commonStyles,
    tableContainer: {
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        overflowY: 'hidden',
        filter: 'none',
        '& .MuiTableContainer-root': {
            maxHeight: '91%',
        },
    },
    table: {
        minWidth: 650,
        '& .MuiTableCell-body': {
            paddingTop: 5,
            paddingBottom: 5,
        },
    },
    badge: {
        minWidth: 120,
        '& .MuiChip-label': {
            width: '100%',
            textAlign: 'center',
        },
    },
    headerContainer: {
        display: 'flex',
        flexDirection: 'column',
        paddingTop: 8,
    },
    headerTitle: {
        fontSize: 20,
        fontWeight: 700,
        color: theme.palette.primary.dark,
    },
    headerSubtitle: {
        marginTop: 6,
        color: theme.palette.text.secondary,
        fontSize: 14,
    },
    NotFoundMessageCell: {
        '&:hover': {
            backgroundColor: 'transparent !important',
        },
    },
    registeredFilterRoot: {
        paddingLeft: '0px !important',
        marginTop: 28,
        '& .MuiInputAdornment-root.MuiInputAdornment-positionStart': {
            position: 'absolute',
            top: '50%',
            right: 0,
        },
        '& .MuiFormLabel-root': {
            fontSize: 14,
        },
        '& .MuiSelect-select:focus': {
            backgroundColor: 'transparent',
        },
        '& .MuiSvgIcon-root': {
            display: 'none',
        },
        '& .MuiListItem-root, & .MuiChip-root': {
            cursor: 'pointer',
        },
        '& .MuiSelect-select.MuiSelect-select': {
            padding: '0 0 0 1px',
            marginTop: 13,
        },
    },
    registeredFilterLabel: {
        fontWeight: 500,
        fontSize: 14,
        color: theme.palette.action.disabled,
        cursor: 'pointer',
        marginTop: 15,
        zIndex: 1,
        '&::after': {
            content: '""',
            display: 'inline-block',
            backgroundImage: `url('${images.icons.lens}')`,
            backgroundSize: 12,
            height: 12,
            width: 12,
            marginLeft: 10,
        },
    },
    registeredFilterFormControl: {
        position: 'absolute',
        left: 0,
        zIndex: 0,
    },
    registeredFilterChips: {
        display: 'flex',
        gap: 6,
        width: 'fit-content',
        cursor: 'pointer',
        '& div, & span': {
            cursor: 'pointer',
        },
    },
    registeredFilterSelectAll: {
        cursor: 'pointer',
        '& .MuiChip-label': {
            color: `${theme.palette.primary.main} !important`,
        },
    },
    registeredFilterSelectAllSelected: {
        cursor: 'pointer',
        '& .MuiChip-label': {
            color: `${theme.palette.common.white} !important`,
        },
    },
    registeredFilterDropdownAllChip: {
        width: 133,
        '& .MuiChip-label': {
            width: '100%',
            textAlign: 'center',
        },
    },
    registeredFilterDropdown: {
        width: 199,
        '& li': {
            justifyContent: 'center',
        },
    },
    registeredFilterOptionChip: {
        width: 133,
        '& .MuiChip-label': {
            width: '100%',
            textAlign: 'center',
        },
    },
    registeredFilterInputChip: {
        width: 'auto',
    },
});

const orderNames: string[] = [
    'name',
    'email',
    'phoneNumber',
    'status',
    'priceLevel',
    'numberOfOrders',
];

const priceLevelColors = ['#7B9AE9', '#5FCF55', '#FFB74D', '#B39DDB', '#80CBC4', '#90A4AE'];
const isRegisteredOptions = [true, false];

function getPriceLevelColor(name: string) {
    if (!name) {
        return theme.palette.grey[400];
    }

    const charSum = name.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
    return priceLevelColors[charSum % priceLevelColors.length];
}

interface RegisteredFilterProps {
    selectedValues: boolean[];
    onOptionToggle: (value: boolean[]) => void;
    classes: ReturnType<typeof useStyles>;
}

function RegisteredFilter(props: RegisteredFilterProps) {
    const [showSelect, setShowSelect] = useState(false);
    const selectedValues = props.selectedValues.map((value) => value.toString());
    const isAllSelected =
        props.selectedValues.length === 0
        || props.selectedValues.length === isRegisteredOptions.length;

    function preventActions(e: React.MouseEvent) {
        e.preventDefault();
        e.stopPropagation();
    }

    function handleOptionSelect(option: 'selectAll' | 'true' | 'false') {
        if (option === 'selectAll') {
            props.onOptionToggle([]);
            setShowSelect(false);
            return;
        }

        const optionValue = option === 'true';
        const nextValues = props.selectedValues.includes(optionValue)
            ? props.selectedValues.filter((value) => value !== optionValue)
            : [...props.selectedValues, optionValue];

        props.onOptionToggle(nextValues);
        setShowSelect(false);
    }

    return (
        <Toolbar className={`${props.classes.registeredFilterRoot} search-input`}>
            {props.selectedValues.length === 0 && (
                <span
                    className={props.classes.registeredFilterLabel}
                    onClick={(e) => {
                        preventActions(e);
                        setShowSelect(true);
                    }}
                >
                    {i18n.t('common.status')}
                </span>
            )}

            <FormControl
                className={props.classes.registeredFilterFormControl}
                onClick={(e) => {
                    e.stopPropagation();
                    setShowSelect(true);
                }}
            >
                <Select
                    multiple
                    open={showSelect}
                    disableUnderline
                    value={selectedValues}
                    onClose={() => {
                        setShowSelect(false);
                    }}
                    input={<Input id="select-multiple-chip" />}
                    renderValue={() => (
                        <div
                            className={props.classes.registeredFilterChips}
                            onClick={() => setShowSelect(!showSelect)}
                        >
                            {!isAllSelected && (
                                <>
                                    {props.selectedValues.includes(true) && (
                                        <StyledChip
                                            size="small"
                                            label={i18n.t('common.registered')}
                                            className={props.classes.registeredFilterInputChip}
                                            style={{ backgroundColor: theme.palette.success.main }}
                                        />
                                    )}
                                    {props.selectedValues.includes(false) && (
                                        <StyledChip
                                            size="small"
                                            label={i18n.t('common.notRegistered')}
                                            className={props.classes.registeredFilterInputChip}
                                            style={{ backgroundColor: theme.palette.grey[400] }}
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    )}
                    MenuProps={{
                        anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                        },
                        transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                        },
                        classes: {
                            paper: props.classes.registeredFilterDropdown,
                            list: props.classes.registeredFilterDropdown,
                        },
                        getContentAnchorEl: null,
                    }}
                >
                    <MenuItem
                        value="selectAll"
                        className={isAllSelected ? 'Mui-selected' : ''}
                        onClick={(e) => {
                            preventActions(e);
                            handleOptionSelect('selectAll');
                        }}
                    >
                        {isAllSelected ? (
                            <Chip
                                size="small"
                                color="primary"
                                label={i18n.t('common.all')}
                                className={`${props.classes.registeredFilterSelectAllSelected} ${props.classes.registeredFilterDropdownAllChip}`}
                            />
                        ) : (
                            <Chip
                                size="small"
                                color="primary"
                                variant="outlined"
                                label={i18n.t('common.all')}
                                className={`${props.classes.registeredFilterSelectAll} ${props.classes.registeredFilterDropdownAllChip}`}
                            />
                        )}
                    </MenuItem>
                    <MenuItem
                        value="true"
                        onClick={(e) => {
                            preventActions(e);
                            handleOptionSelect('true');
                        }}
                    >
                        <StyledChip
                            size="small"
                            label={i18n.t('common.registered')}
                            className={props.classes.registeredFilterOptionChip}
                            style={{ backgroundColor: theme.palette.success.main }}
                        />
                    </MenuItem>
                    <MenuItem
                        value="false"
                        onClick={(e) => {
                            preventActions(e);
                            handleOptionSelect('false');
                        }}
                    >
                        <StyledChip
                            size="small"
                            label={i18n.t('common.notRegistered')}
                            className={props.classes.registeredFilterOptionChip}
                            style={{ backgroundColor: theme.palette.grey[400] }}
                        />
                    </MenuItem>
                </Select>
            </FormControl>
        </Toolbar>
    );
}

export default function CustomerManagementTable() {
    const classes = useStyles();
    const dispatch = useDispatch();
    const [customers, setCustomers] = useState<CustomerManagementListItemData[]>([]);
    const [count, setCount] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(pagingOptions[0]);
    const [orderBy, setOrderBy] = useState(orderNames[0]);
    const [asc, setAsc] = useState(true);
    const defaultFilter: CustomerManagementFilterModel = useMemo(
        () => ({
            name: '',
            email: '',
            phoneNumber: '',
            isRegistered: [],
            priceLevel: '',
            numberOfOrders: 0,
        }),
        []
    );
    const [filter, setFilter] = useState(defaultFilter);

    function getCustomers(
        targetFilter: CustomerManagementFilterModel,
        targetPage: number,
        targetRowsPerPage: number,
        targetAsc: boolean,
        targetOrderBy: string
    ) {
        globalStore.actionCreators.showSpiner()(dispatch);
        return agent.Restaurants.GetRestaurantCustomers(
            targetFilter,
            targetPage,
            targetRowsPerPage,
            targetAsc,
            targetOrderBy
        )
            .then((response: CustomerManagementPagedModel) => {
                setCustomers(response.list ?? []);
                setCount(response.count ?? 0);
            })
            .catch((e) => {
                globalStore.actionCreators.showToaster('error', e)(dispatch);
            })
            .finally(() => globalStore.actionCreators.hideSpiner()(dispatch));
    }

    function handleChangePage(event: unknown, newPage: number) {
        setPage(newPage);
        getCustomers(filter, newPage, rowsPerPage, asc, orderBy);
    }

    function handleChangeRowsPerPage(event: React.ChangeEvent<HTMLInputElement>) {
        const targetRowsPerPage = +event.target.value;
        setRowsPerPage(targetRowsPerPage);
        setPage(0);
        getCustomers(filter, 0, targetRowsPerPage, asc, orderBy);
    }

    function handleOrder(id: string) {
        if (orderBy === id) {
            const targetAsc = !asc;
            setAsc(targetAsc);
            getCustomers(filter, page, rowsPerPage, targetAsc, orderBy);
        } else {
            setOrderBy(id);
            getCustomers(filter, page, rowsPerPage, asc, id);
        }
    }

    function handleFilterChange() {
        setPage(0);
        getCustomers(filter, 0, rowsPerPage, asc, orderBy);
    }

    function clearFilterField(value: keyof CustomerManagementFilterModel) {
        if (filter.hasOwnProperty(value)) {
            const nextFilter = {
                ...filter,
                [value]: value === 'numberOfOrders' ? 0 : value === 'isRegistered' ? [] : '',
            };
            setFilter(nextFilter);
            setPage(0);
            getCustomers(nextFilter, 0, rowsPerPage, asc, orderBy);
        }
    }

    function handleIsRegisteredFilterChange(nextIsRegistered: boolean[]) {
        const normalizedIsRegistered =
            nextIsRegistered.length === isRegisteredOptions.length ? [] : nextIsRegistered;
        const nextFilter: CustomerManagementFilterModel = {
            ...filter,
            isRegistered: normalizedIsRegistered,
        };
        setFilter(nextFilter);
        setPage(0);
        getCustomers(nextFilter, 0, rowsPerPage, asc, orderBy);
    }

    function resetFilter() {
        setFilter(defaultFilter);
        setPage(0);
        setOrderBy(orderNames[0]);
        setAsc(true);
        getCustomers(defaultFilter, 0, rowsPerPage, true, orderNames[0]);
    }

    function getThreeDotsItems(row: CustomerManagementListItemData) {
        const items = [
            <div className={classes.threeDotsMenuItemContainer}>
                <ListItemIcon>
                    <HighlightOffOutlinedIcon fontSize="small" color="disabled" />
                </ListItemIcon>
                <ListItemText primary={i18n.t('common.revokeSpecialPricing')} />
            </div>,
        ];

        if (!row.isRegistered) {
            items.push(
                <div className={classes.threeDotsMenuItemContainer}>
                    <ListItemIcon>
                        <EmailOutlinedIcon fontSize="small" color="disabled" />
                    </ListItemIcon>
                    <ListItemText primary={i18n.t('common.sendRegistrationEmail')} />
                </div>
            );
        }

        return items;
    }

    useEffect(() => {
        getCustomers(defaultFilter, page, rowsPerPage, asc, orderBy);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <TableToolbar
                leftActions={
                    <div className={classes.headerContainer}>
                        <Typography variant="h4" align="left" className={classes.headerTitle}>
                            {i18n.t('common.customerManagementTitle')}
                        </Typography>
                        <Typography variant="h5" className={classes.headerSubtitle}>
                            {i18n.t('common.customerManagementDescription')}
                        </Typography>
                    </div>
                }
                rightActions={[
                    <Button variant="outlined" color="primary" onClick={resetFilter}>
                        {i18n.t('form.resetSearch')}
                    </Button>,
                ]}
            />
            <Paper className={classes.tableContainer}>
                <TableContainer>
                    <Table stickyHeader className={classes.table}>
                        <TableHead>
                            <TableRow key="table-head">
                                <TableCell sortDirection={asc ? 'asc' : 'desc'}>
                                    <TableSortLabel
                                        active={orderBy === orderNames[0]}
                                        direction={asc ? 'asc' : 'desc'}
                                        onClick={() => handleOrder(orderNames[0])}
                                    >
                                        <SearchInput
                                            value={filter.name}
                                            name={i18n.t('form.customerName')}
                                            trimValue={false}
                                            onChange={(value: string) =>
                                                setFilter({ ...filter, name: value })
                                            }
                                            onEnter={handleFilterChange}
                                            onReset={() => clearFilterField('name')}
                                        />
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={asc ? 'asc' : 'desc'}>
                                    <TableSortLabel
                                        active={orderBy === orderNames[1]}
                                        direction={asc ? 'asc' : 'desc'}
                                        onClick={() => handleOrder(orderNames[1])}
                                    >
                                        <SearchInput
                                            value={filter.email}
                                            name={i18n.t('form.email')}
                                            onChange={(value: string) =>
                                                setFilter({ ...filter, email: value })
                                            }
                                            onEnter={handleFilterChange}
                                            onReset={() => clearFilterField('email')}
                                        />
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={asc ? 'asc' : 'desc'}>
                                    <TableSortLabel
                                        active={orderBy === orderNames[2]}
                                        direction={asc ? 'asc' : 'desc'}
                                        onClick={() => handleOrder(orderNames[2])}
                                    >
                                        <SearchInput
                                            value={filter.phoneNumber}
                                            name={i18n.t('form.phoneNumber')}
                                            onChange={(value: string) =>
                                                setFilter({ ...filter, phoneNumber: value })
                                            }
                                            onEnter={handleFilterChange}
                                            onReset={() => clearFilterField('phoneNumber')}
                                        />
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={asc ? 'asc' : 'desc'}>
                                    <TableSortLabel
                                        active={orderBy === orderNames[3]}
                                        direction={asc ? 'asc' : 'desc'}
                                        onClick={() => handleOrder(orderNames[3])}
                                    >
                                        <RegisteredFilter
                                            selectedValues={filter.isRegistered}
                                            onOptionToggle={handleIsRegisteredFilterChange}
                                            classes={classes}
                                        />
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={asc ? 'asc' : 'desc'}>
                                    <TableSortLabel
                                        active={orderBy === orderNames[4]}
                                        direction={asc ? 'asc' : 'desc'}
                                        onClick={() => handleOrder(orderNames[4])}
                                    >
                                        <SearchInput
                                            value={filter.priceLevel}
                                            name={i18n.t('common.priceLevel')}
                                            onChange={(value: string) =>
                                                setFilter({ ...filter, priceLevel: value })
                                            }
                                            onEnter={handleFilterChange}
                                            onReset={() => clearFilterField('priceLevel')}
                                        />
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={asc ? 'asc' : 'desc'}>
                                    <TableSortLabel
                                        active={orderBy === orderNames[5]}
                                        direction={asc ? 'asc' : 'desc'}
                                        onClick={() => handleOrder(orderNames[5])}
                                    >
                                        <SearchInput
                                            value={
                                                !!filter.numberOfOrders
                                                    ? filter.numberOfOrders.toString()
                                                    : ''
                                            }
                                            name={i18n.t('restaurant.numberOfOrders')}
                                            onChange={(value: string) =>
                                                setFilter({
                                                    ...filter,
                                                    numberOfOrders: parseInt(value) || 0,
                                                })
                                            }
                                            onEnter={handleFilterChange}
                                            onReset={() => clearFilterField('numberOfOrders')}
                                        />
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {customers.length > 0 ? (
                                customers.map((row, index) => (
                                    <TableRow key={`${row.email}-${row.phoneNumber}-${index}`}>
                                        <TableCell style={{ width: 220, maxWidth: 220 }}>
                                            <Text
                                                text={row.name || '-'}
                                                maxLength={20}
                                                useClassic
                                            />
                                        </TableCell>
                                        <TableCell style={{ width: 180, maxWidth: 180 }}>
                                            <Text
                                                text={row.email || '-'}
                                                maxLength={25}
                                                useClassic
                                            />
                                        </TableCell>
                                        <TableCell>{row.phoneNumber || '-'}</TableCell>
                                        <TableCell>
                                            <StyledChip
                                                size="small"
                                                label={
                                                    row.isRegistered
                                                        ? i18n.t('common.registered')
                                                        : i18n.t('common.notRegistered')
                                                }
                                                className={classes.badge}
                                                style={{
                                                    backgroundColor: row.isRegistered
                                                        ? theme.palette.success.main
                                                        : theme.palette.grey[400],
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {row.priceLevel ? (
                                                <StyledChip
                                                    size="small"
                                                    label={row.priceLevel}
                                                    className={classes.badge}
                                                    style={{
                                                        backgroundColor: getPriceLevelColor(
                                                            row.priceLevel
                                                        ),
                                                    }}
                                                />
                                            ) : (
                                                '-'
                                            )}
                                        </TableCell>
                                        <TableCell>{row.ordersCount}</TableCell>
                                        <TableCell align="left">
                                            <BasicThreeDotsMenu items={getThreeDotsItems(row)} />
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow className={classes.NotFoundMessageCell}>
                                    <TableCell colSpan={7} style={{ height: 550 }}>
                                        <NothingFoundMessage />
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={pagingOptions}
                    component="div"
                    count={count}
                    rowsPerPage={rowsPerPage}
                    labelRowsPerPage={i18n.t('common.rowsPerPage')}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
        </>
    );
}
